import GenericCalculator from "../components/GenericCalculator";
import { Ruler, Box } from "lucide-react";
import { BP_THEME, COLOUR_MAP, BlueprintDefs, BlueprintFrame, DimH, DimV, BlueprintFooter } from "../components/blueprint";

function StepPreview({ width = 600, depth = 300, height = 150, steps = 1, colour = "Natural Black" }) {
  const CW = 560, CH = 320;
  const PAD_L = 70, PAD_R = 40, PAD_T = 50, PAD_B = 80;
  const stepCount = Math.max(parseInt(steps) || 1, 1);
  const W = Math.max(Number(width) || 0, 100);
  const D = Math.max(Number(depth) || 0, 100);
  const H = Math.max(Number(height) || 0, 30);
  const totalD = D * stepCount;
  const totalH = H * stepCount;
  const innerW = CW - PAD_L - PAD_R, innerH = CH - PAD_T - PAD_B;
  const scale = Math.min(innerW / totalD, innerH / totalH);
  const stepD = D * scale, stepH = H * scale;
  const totalSW = stepD * stepCount;
  const totalSH = stepH * stepCount;
  const startX = PAD_L + (innerW - totalSW) / 2;
  const baseY = PAD_T + (innerH + totalSH) / 2;
  const fill = COLOUR_MAP[colour] || "#1f2330";
  const ID = "st";
  return (
    <svg viewBox={`0 0 ${CW} ${CH}`} className="w-full h-auto" style={{ maxHeight: 320, background: BP_THEME.bg, borderRadius: 6 }} data-testid="step-preview">
      <BlueprintDefs id={ID} fill={fill} />
      <BlueprintFrame id={ID} w={CW} h={CH} title={`SIDE VIEW · STEP / PLATFORM · ${stepCount} STEP${stepCount > 1 ? "S" : ""}`} />
      {Array.from({ length: stepCount }).map((_, i) => {
        const idx = stepCount - 1 - i;
        const x = startX + idx * stepD;
        const y = baseY - (i + 1) * stepH;
        const h = (i + 1) * stepH;
        return (
          <g key={`step-${i}`}>
            <rect x={x} y={y} width={stepD} height={h} fill={`url(#fill-${ID})`} stroke={BP_THEME.accent} strokeWidth="1.4" opacity={0.92 - i * 0.05} />
            <text x={x + stepD / 2} y={y + 12} textAnchor="middle" fontSize="9" fill={BP_THEME.accent} fontFamily={BP_THEME.fontMono}>STEP {i + 1}</text>
          </g>
        );
      })}
      <DimH id={ID} x1={startX} x2={startX + totalSW} y={baseY + 28} label={`TOTAL D = ${Math.round(D * stepCount)} mm`} />
      <DimV id={ID} x={startX - 22} y1={baseY - totalSH} y2={baseY} label={`TOTAL H = ${Math.round(H * stepCount)} mm`} />
      <text x={CW - PAD_R - 4} y={baseY - totalSH - 8} fontSize="9" fill={BP_THEME.accent} fontFamily={BP_THEME.fontMono} textAnchor="end">⌀ W = {Math.round(W)}mm  ·  RISE/STEP {Math.round(H)}mm  ·  TREAD {Math.round(D)}mm</text>
      <BlueprintFooter w={CW} y={CH - 12} left={`MATERIAL · RUBBER STEP · ${colour.toUpperCase()}`} />
    </svg>
  );
}

const FIELD_GROUPS = [
  {
    title: "Dimensions (mm)", icon: Ruler,
    fields: [
      { key: "width", label: "Width", type: "number" },
      { key: "depth", label: "Depth", type: "number" },
      { key: "height", label: "Height", type: "number" },
      { key: "steps", label: "Number of Steps", type: "number", min: 1 },
    ],
  },
  {
    title: "Options", icon: Box,
    fields: [
      { key: "left_kerb", label: "Left Kerb", type: "yn" },
      { key: "right_kerb", label: "Right Kerb", type: "yn" },
      { key: "notches", label: "Notches", type: "number" },
      { key: "quantity", label: "Quantity", type: "number" },
      { key: "colour", label: "Colour", type: "colour" },
    ],
  },
];

const DEFAULTS = {
  width: "", depth: "", height: "", steps: 1,
  left_kerb: false, right_kerb: false,
  notches: "", quantity: 1, colour: "Natural Black",
};

export default function StepCalc() {
  return (
    <GenericCalculator
      productType="step"
      productLabel="Rubber Step / Platform Calculator"
      defaultInputs={DEFAULTS}
      fieldGroups={FIELD_GROUPS}
      PreviewSvg={StepPreview}
      endpoint="/calculate/step"
    />
  );
}
